export type FilterOperator = 'eq' | 'ne' | 'gt' | 'ge' | 'lt' | 'le' | 'contains' | 'startswith' | 'endswith'

export class ODataBuilder {
    private params: URLSearchParams

    constructor() {
        this.params = new URLSearchParams()
    }

    /**
     * Seleciona campos específicos ($select)
     */
    select(fields: string[]): this {
        this.params.set('$select', fields.join(','))
        return this
    }

    /**
     * Expande relacionamentos ($expand)
     */
    expand(relations: string[]): this {
        this.params.set('$expand', relations.join(','))
        return this
    }

    /**
     * Ordenação ($orderby)
     */
    orderBy(field: string, direction: 'asc' | 'desc' = 'asc'): this {
        const current = this.params.get('$orderby')
        const newSort = `${field} ${direction}`
        this.params.set('$orderby', current ? `${current},${newSort}` : newSort)
        return this
    }

    /**
     * Paginação: Topo ($top)
     */
    top(n: number): this {
        this.params.set('$top', n.toString())
        return this
    }

    /**
     * Paginação: Pular ($skip)
     */
    skip(n: number): this {
        this.params.set('$skip', n.toString())
        return this
    }

    /**
     * Paginação Simplificada (Page/Size)
     */
    paginate(page: number, size: number): this {
        this.top(size)
        this.skip((page - 1) * size)
        return this
    }

    /**
     * Filtros Simples ($filter)
     * Ex: filter('Name', 'eq', 'João') -> Name eq 'João'
     */
    filter(field: string, operator: FilterOperator, value: string | number | boolean | null): this {
        let formattedValue = value

        if (typeof value === 'string') formattedValue = `'${value.replace(/'/g, "''")}'`
        if (value === null) formattedValue = 'null'

        const expression = `${field} ${operator} ${formattedValue}`
        this.addFilterExpression(expression)
        return this
    }

    /**
     * Filtros de String Específicos (contains, startswith)
     * Ex: contains('Name', 'Jo') -> contains(Name, 'Jo')
     */
    filterString(method: 'contains' | 'startswith' | 'endswith', field: string, value: string): this {
        const expression = `${method}(${field}, '${value}')`
        this.addFilterExpression(expression)
        return this
    }

    /**
     * Adiciona um filtro raw manual (para lógicas complexas com OR/AND aninhados)
     */
    rawFilter(expression: string): this {
        this.addFilterExpression(expression)
        return this
    }

    /**
     * Helper interno para concatenar filtros com 'and'
     */
    private addFilterExpression(expression: string) {
        const current = this.params.get('$filter')
        if (current) {
            this.params.set('$filter', `${current} and ${expression}`)
        } else {
            this.params.set('$filter', expression)
        }
    }

    /**
     * Retorna o objeto pronto para o ofetch
     */
    build(): Record<string, string> {
        const query: Record<string, string> = {}
        this.params.forEach((value, key) => {
            query[key] = value
        })
        return query
    }
}

// Factory function para facilitar o uso sem 'new'
export const odata = () => new ODataBuilder()